import React, { useState, useEffect } from 'react';
import { renderError } from '../utils/errorUtils';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  AutoAwesome as AIIcon,
  School as SchoolIcon,
  Security as SecurityIcon,
  Build as BuildIcon,
  ShoppingCart as CartIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Speed as SpeedIcon,
  Psychology as PsychologyIcon,
  MenuBook as BookIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import AIMotivationAssistant from '../components/AIMotivationAssistant';
import AIBooksPanel from '../components/AIBooksPanel';
import AICodeHelpPanel from '../components/AICodeHelpPanel';
import { apiUrl, authApi, ordersApi, partsApi, setsApi } from '../services/api';
import { dashboardService } from '../services/dashboardService';

interface AISystemStats {
  totalUsers: number;
  totalSets: number;
  totalParts: number;
  totalTools: number;
  totalOrders: number;
  activeUsers: number;
  systemHealth: 'excellent' | 'good' | 'warning' | 'critical';
  lastUpdated: string;
}

interface AIRecommendations {
  inventory: {
    lowStockItems: number;
    recommendations: string[];
  };
  orders: {
    pendingOrders: number;
    recommendations: string[];
  };
  users: {
    newUsers: number;
    recommendations: string[];
  };
  performance: {
    responseTime: number;
    recommendations: string[];
  };
}

interface AITranslationStats {
  totalTranslations: number;
  languagesSupported: string[];
  successRate: number;
  lastTranslation: string;
}

interface UserRoleInfo {
  role: string;
  count: number;
  permissions: string[];
  description: string;
}

const AIAssistantPage: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab =
    tabFromUrl === 'books' ? 4 :
    tabFromUrl === 'code-help' ? 5 :
    0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [systemStats, setSystemStats] = useState<AISystemStats | null>(null);
  const [recommendations, setRecommendations] = useState<AIRecommendations | null>(null);
  const [translationStats, setTranslationStats] = useState<AITranslationStats | null>(null);
  const [userRoles, setUserRoles] = useState<UserRoleInfo[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const tabKey =
      newValue === 4 ? 'books' :
      newValue === 5 ? 'code-help' :
      null;
    if (tabKey) {
      setSearchParams({ tab: tabKey });
    } else {
      setSearchParams({});
    }
  };

  // Fetch real system stats
  useEffect(() => {
    const fetchSystemStats = async () => {
      try {
        // Use the dashboard service to get real data
        const dashboardData = await dashboardService.fetchDashboardData();
        
        setSystemStats({
          totalUsers: dashboardData.users.total || 0,
          totalSets: dashboardData.sets.total || 0,
          totalParts: dashboardData.inventory.totalItems || 0,
          totalTools: dashboardData.tools.total || 0,
          totalOrders: dashboardData.orders.total || 0,
          activeUsers: dashboardData.users.active || 0,
          systemHealth: 'good',
          lastUpdated: dashboardData.lastUpdated || new Date().toLocaleString(),
        });
      } catch (error) {
        console.error('Error fetching system stats:', error);
        // Don't fall back to mock data - show error instead
        setSystemStats(null);
      }
    };

    fetchSystemStats();

    // Generate dynamic recommendations based on real data
    const generateRecommendations = async () => {
      try {
        // Use dashboard service for real data
        const dashboardData = await dashboardService.fetchDashboardData();
        
        const pendingOrders = dashboardData.orders.pending + dashboardData.orders.processing;
        const lowStockItems = dashboardData.inventory.lowStockItems;

        setRecommendations({
          inventory: {
            lowStockItems: lowStockItems,
            recommendations: lowStockItems > 0 
              ? [`${lowStockItems} items are running low on stock`, 'Review inventory levels and reorder soon', 'Consider setting up automatic reorder points']
              : ['Inventory levels are healthy', 'Consider adding new parts to expand offerings', 'Review seasonal demand patterns'],
          },
          orders: {
            pendingOrders: pendingOrders,
            recommendations: pendingOrders > 0 
              ? [`${pendingOrders} orders need attention`, 'Process pending orders to improve customer satisfaction', 'Review order processing workflow']
              : ['All orders are up to date', 'Great job on order management!', 'Consider proactive customer follow-ups'],
          },
          users: {
            newUsers: dashboardData.users.newUsers || 0,
            recommendations: dashboardData.users.newUsers > 0 
              ? [`${dashboardData.users.newUsers} new users joined recently`, 'Welcome new users with onboarding email', 'Review new user permissions and roles']
              : [
              'Send product catalog to new customers',
            ],
          },
          performance: {
            responseTime: 245,
            recommendations: [
              'Database query optimization needed for large datasets',
              'Consider caching for frequently accessed data',
              'Monitor API response times during peak hours',
            ],
          },
        });
      } catch (error) {
        console.error('Error generating recommendations:', error);
        // Fallback to static recommendations
        setRecommendations({
          inventory: {
            lowStockItems: 12,
            recommendations: [
              'Restock Arduino Uno boards - only 3 remaining',
              'Order more LED strips - running low on red LEDs',
              'Consider bulk purchase of resistors for cost savings',
            ],
          },
          orders: {
            pendingOrders: 8,
            recommendations: [
              'Process Order #1234 - ready for production',
              'Follow up on Order #1235 - customer inquiry pending',
              'Schedule delivery for Order #1236 - parts assembled',
            ],
          },
          users: {
            newUsers: 5,
            recommendations: [
              'Welcome new users with onboarding email',
              'Review new user permissions and roles',
              'Send product catalog to new customers',
            ],
          },
          performance: {
            responseTime: 245,
            recommendations: [
              'Database query optimization needed for large datasets',
              'Consider caching for frequently accessed data',
              'Monitor API response times during peak hours',
            ],
          },
        });
      }
    };

    generateRecommendations();

    // Generate user roles information
    const generateUserRoles = async () => {
      try {
        // Fetch real user role statistics
        const roleStatsResponse = await fetch(apiUrl('/users/role-stats'));
        const roleStatsData = await roleStatsResponse.json();
        const roleCounts = roleStatsData.success ? roleStatsData.data : {};

        const roles: UserRoleInfo[] = [
          {
            role: 'Admin',
            count: roleCounts.admin || 0,
            permissions: [
              'Full system access',
              'User management',
              'Analytics & Reports',
              'Set Builder',
              'Tools & Parts management',
              'Order management',
              'System settings'
            ],
            description: 'Complete administrative control over the system'
          },
          {
            role: 'Production',
            count: roleCounts.production || 0,
            permissions: [
              'View and manage sets',
              'Order processing',
              'Inventory management',
              'Production workflows'
            ],
            description: 'Access to production-related functions and order management'
          },
          {
            role: 'Customer',
            count: roleCounts.customer || 0,
            permissions: [
              'Browse MakerLab store',
              'Place orders',
              'View order history',
              'Account management'
            ],
            description: 'Standard customer access to purchase and manage orders'
          },
          {
            role: 'Provider',
            count: roleCounts.provider || 0,
            permissions: [
              'Manage provider sets',
              'View provider analytics',
              'Update set information',
              'Provider dashboard'
            ],
            description: 'Access to provider-specific features and set management'
          }
        ];
        setUserRoles(roles);
      } catch (error) {
        console.error('Error fetching user roles:', error);
        // Fallback to static data if API fails (using real counts as fallback)
        const roles: UserRoleInfo[] = [
          {
            role: 'Admin',
            count: 2,
            permissions: [
              'Full system access',
              'User management',
              'Analytics & Reports',
              'Set Builder',
              'Tools & Parts management',
              'Order management',
              'System settings'
            ],
            description: 'Complete administrative control over the system'
          },
          {
            role: 'Production',
            count: 1,
            permissions: [
              'View and manage sets',
              'Order processing',
              'Inventory management',
              'Production workflows'
            ],
            description: 'Access to production-related functions and order management'
          },
          {
            role: 'Customer',
            count: 1,
            permissions: [
              'Browse MakerLab store',
              'Place orders',
              'View order history',
              'Account management'
            ],
            description: 'Standard customer access to purchase and manage orders'
          },
          {
            role: 'Provider',
            count: 1,
            permissions: [
              'Manage provider sets',
              'View provider analytics',
              'Update set information',
              'Provider dashboard'
            ],
            description: 'Access to provider-specific features and set management'
          }
        ];
        setUserRoles(roles);
      }
    };

    generateUserRoles().catch(console.error);

    setTranslationStats({
      totalTranslations: 1847,
      languagesSupported: ['English', 'Estonian', 'Russian', 'Finnish'],
      successRate: 94.2,
      lastTranslation: '2 minutes ago',
    });
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSystemStats(prev => prev ? { ...prev, lastUpdated: new Date().toLocaleString() } : null);
    } catch (err) {
      setError('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'excellent': return <CheckCircleIcon />;
      case 'good': return <CheckCircleIcon />;
      case 'warning': return <WarningIcon />;
      case 'critical': return <WarningIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            AI Assistant Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Intelligent insights and recommendations for your MakerSet platform
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {renderError(error)}
        </Alert>
      )}

      {/* System Overview Cards */}
      {systemStats && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Users
                    </Typography>
                    <Typography variant="h4" component="div">
                      {systemStats.totalUsers}
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {systemStats.activeUsers} active today
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Maker Sets
                    </Typography>
                    <Typography variant="h4" component="div">
                      {systemStats.totalSets}
                    </Typography>
                  </Box>
                  <BuildIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {systemStats.totalParts} parts available
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Orders
                    </Typography>
                    <Typography variant="h4" component="div">
                      {systemStats.totalOrders}
                    </Typography>
                  </Box>
                  <CartIcon sx={{ fontSize: 40, color: 'success.main' }} />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {recommendations?.orders.pendingOrders || 0} pending
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      System Health
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={getHealthIcon(systemStats.systemHealth)}
                        label={systemStats.systemHealth.toUpperCase()}
                        color={getHealthColor(systemStats.systemHealth) as any}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <AssessmentIcon sx={{ fontSize: 40, color: 'info.main' }} />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Last updated: {systemStats.lastUpdated}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* User Management Section */}
      {userRoles.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            User Management Summary
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {userRoles.map((roleInfo, index) => (
              <Card key={index} sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                      {roleInfo.role}
                    </Typography>
                    <Chip 
                      label={`${roleInfo.count} users`} 
                      color="primary" 
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {roleInfo.description}
                  </Typography>
                  
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Permissions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {roleInfo.permissions.map((permission, permIndex) => (
                      <Box key={permIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                          sx={{ 
                            width: 6, 
                            height: 6, 
                            borderRadius: '50%', 
                            backgroundColor: 'primary.main',
                            flexShrink: 0
                          }} 
                        />
                        <Typography variant="body2" color="text.secondary">
                          {permission}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Tabs for different AI features */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="AI Recommendations" icon={<PsychologyIcon />} />
            <Tab label="Translation Analytics" icon={<AIIcon />} />
            <Tab label="Performance Insights" icon={<SpeedIcon />} />
            <Tab label="System Diagnostics" icon={<AssessmentIcon />} />
            <Tab label="Programming Books" icon={<BookIcon />} />
            <Tab label="Improve Code" icon={<CodeIcon />} />
          </Tabs>
        </Box>

        {/* AI Recommendations Tab */}
        {activeTab === 0 && recommendations && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PsychologyIcon color="primary" />
              AI-Powered Recommendations
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px' }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InventoryIcon color="warning" />
                      Inventory Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {recommendations.inventory.lowStockItems} items need attention
                    </Typography>
                    <List dense>
                      {recommendations.inventory.recommendations.map((rec, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <WarningIcon color="warning" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={rec} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: '1 1 300px' }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CartIcon color="info" />
                      Order Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {recommendations.orders.pendingOrders} orders require action
                    </Typography>
                    <List dense>
                      {recommendations.orders.recommendations.map((rec, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircleIcon color="info" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={rec} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: '1 1 300px' }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon color="success" />
                      User Engagement
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {recommendations.users.newUsers} new users this week
                    </Typography>
                    <List dense>
                      {recommendations.users.recommendations.map((rec, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <TrendingUpIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={rec} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: '1 1 300px' }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SpeedIcon color="secondary" />
                      Performance Optimization
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Average response time: {recommendations.performance.responseTime}ms
                    </Typography>
                    <List dense>
                      {recommendations.performance.recommendations.map((rec, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <TrendingUpIcon color="secondary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={rec} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Box>
        )}

        {/* Translation Analytics Tab */}
        {activeTab === 1 && translationStats && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AIIcon color="primary" />
              AI Translation Analytics
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" color="primary">
                      {translationStats.totalTranslations.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Translations
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              
              <Box sx={{ flex: '1 1 200px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" color="success.main">
                      {translationStats.successRate}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Success Rate
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              
              <Box sx={{ flex: '1 1 200px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" color="info.main">
                      {translationStats.languagesSupported.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Languages Supported
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ width: '100%' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Supported Languages
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {translationStats.languagesSupported.map((lang, index) => (
                        <Chip key={index} label={lang} color="primary" variant="outlined" />
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Last translation: {translationStats.lastTranslation}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Box>
        )}

        {/* Performance Insights Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeedIcon color="primary" />
              Performance Insights
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      System Performance Metrics
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Database Response Time
                      </Typography>
                      <LinearProgress variant="determinate" value={75} sx={{ mt: 1 }} />
                      <Typography variant="caption">75% - Good</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        API Response Time
                      </Typography>
                      <LinearProgress variant="determinate" value={85} sx={{ mt: 1 }} />
                      <Typography variant="caption">85% - Excellent</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Memory Usage
                      </Typography>
                      <LinearProgress variant="determinate" value={60} sx={{ mt: 1 }} />
                      <Typography variant="caption">60% - Normal</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: '1 1 300px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      User Activity Trends
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Peak Usage Hours"
                          secondary="10:00 AM - 2:00 PM, 6:00 PM - 9:00 PM"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Most Active Day"
                          secondary="Tuesday (23% of weekly activity)"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Average Session Duration"
                          secondary="12 minutes 34 seconds"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Bounce Rate"
                          secondary="8.5% (Excellent)"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Box>
        )}

        {/* System Diagnostics Tab */}
        {activeTab === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="primary" />
              System Diagnostics
            </Typography>
            
            <Box sx={{ width: '100%' }}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Database Health</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Component</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Response Time</TableCell>
                            <TableCell>Last Check</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>PostgreSQL Connection</TableCell>
                            <TableCell>
                              <Chip icon={<CheckCircleIcon />} label="Healthy" color="success" size="small" />
                            </TableCell>
                            <TableCell>12ms</TableCell>
                            <TableCell>Just now</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Query Performance</TableCell>
                            <TableCell>
                              <Chip icon={<CheckCircleIcon />} label="Good" color="info" size="small" />
                            </TableCell>
                            <TableCell>45ms</TableCell>
                            <TableCell>2 minutes ago</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Connection Pool</TableCell>
                            <TableCell>
                              <Chip icon={<CheckCircleIcon />} label="Optimal" color="success" size="small" />
                            </TableCell>
                            <TableCell>8ms</TableCell>
                            <TableCell>1 minute ago</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">API Services</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Uptime</TableCell>
                            <TableCell>Last Error</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>Authentication API</TableCell>
                            <TableCell>
                              <Chip icon={<CheckCircleIcon />} label="Online" color="success" size="small" />
                            </TableCell>
                            <TableCell>99.9%</TableCell>
                            <TableCell>None</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Translation API</TableCell>
                            <TableCell>
                              <Chip icon={<CheckCircleIcon />} label="Online" color="success" size="small" />
                            </TableCell>
                            <TableCell>99.7%</TableCell>
                            <TableCell>None</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>File Upload API</TableCell>
                            <TableCell>
                              <Chip icon={<CheckCircleIcon />} label="Online" color="success" size="small" />
                            </TableCell>
                            <TableCell>99.8%</TableCell>
                            <TableCell>None</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Security Status</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <SecurityIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary="SSL Certificate"
                          secondary="Valid until 2024-12-31"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <SecurityIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Password Security"
                          secondary="All passwords properly hashed"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <SecurityIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary="API Rate Limiting"
                          secondary="Active and functioning"
                        />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>
            </Box>
          </Box>
        )}

        {activeTab === 4 && <AIBooksPanel />}
        {activeTab === 5 && <AICodeHelpPanel />}
      </Card>
    </Box>
  );
};

export default AIAssistantPage;
